import { Navigate, Outlet, useParams } from "react-router";
import { Breadcrumb } from "@/components/breadcrumb";
import { DocButton } from "@/components/ui/doc-button";
import {
  usePortfolioCollection,
  useSustainabilityConfig,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { CollectionTabs } from "@/pages/settings/portfolios/detail/collection-tabs";
import { LayoutSkeleton } from "@/pages/settings/portfolios/detail/skeleton-loaders";
import { T } from "./portfolio-collection-layout.i18n";

/**
 * Layout for portfolio collection detail pages (/settings/portfolios/:id/*).
 * Renders breadcrumb, collection header, tabs, and child content via <Outlet />.
 * Ported from app/settings/portfolios/detail/layout.tsx.
 */
export default function PortfolioCollectionLayout() {
  const { id } = useParams<{ id: string }>();
  const localize = useLocalize();
  const { collection, isLoading } = usePortfolioCollection(id!);
  const { config: sustainabilityConfig } = useSustainabilityConfig(id!);
  const t = useT(T);

  if (isLoading) {
    return <LayoutSkeleton />;
  }

  if (!collection) {
    return <Navigate to="/settings/portfolios" replace />;
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: t.settings, href: "/settings" },
          { label: t.portfolioCollections, href: "/settings/portfolios" },
          { label: localize(collection.nameI18n) },
        ]}
      />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">{localize(collection.nameI18n)}</h1>
          <p className="text-muted-foreground mt-2">
            {localize(collection.descriptionI18n) || t.defaultDescription}
          </p>
        </div>
        <DocButton docSlug="collections" variant="outline" size="sm">
          {t.documentation}
        </DocButton>
      </div>

      <CollectionTabs
        collectionId={id!}
        hasRiskModel={!!collection.riskModel}
        hasSustainability={sustainabilityConfig != null}
        hasDynamicPortfolios={collection.dynamicPortfoliosEnabled}
        hasWrapperTypes={collection.wrapperTypesEnabled}
        hasProductTypes={collection.productTypesEnabled}
        hasMandates={collection.mandatesEnabled}
        tabWarnings={{
          wrapperTypes:
            collection.wrapperTypeStatus != null && !collection.wrapperTypeStatus.hasWrapperTypes,
          products: collection.wrapperTypeStatus?.hasProductsMissingWrapperTypes ?? false,
          // QL-198 — a platform with no active model portfolio tagged can
          // never resolve model advice for any goal on it (QP-226).
          platforms: (collection.platformStatus?.platformIdsWithoutModelPortfolios.length ?? 0) > 0,
          // QL-36 — some client type has no goal type it can pick.
          goalTypes: (collection.goalTypeStatus?.clientTypesWithoutGoalTypes.length ?? 0) > 0,
          // QL-375 — any mandate configuration gap: no goal type to say a goal
          // uses one, a risk score with no active template, a template tagged to
          // no platform, or a template line on an inactive product. The same
          // flags the Mandates tab lists in its warning box.
          mandates:
            collection.mandateStatus != null &&
            (!collection.mandateStatus.hasGoalTypes ||
              collection.mandateStatus.riskScoresWithoutTemplate.length > 0 ||
              collection.mandateStatus.untaggedTemplateIds.length > 0 ||
              collection.mandateStatus.templatesWithInactiveProduct.length > 0),
        }}
      />

      <Outlet />
    </div>
  );
}
